export const agenticDevDE = {
  seo: {
    title: "Intent-Driven Development (IDD) für Agentic Coding | pAIoneer Labs GmbH",
    description: "Intent-Driven Development macht KI-gestützte Softwareentwicklung steuerbar: Bedeutung, Entscheidungen und Szenarien werden zu operativen Artefakten für Agentic Coding.",
    keywords: "Intent-Driven Development, Intent Driven Development, IDD, Agentic Coding, agentische Softwareentwicklung, KI-gestützte Softwareentwicklung, Vibe Coding, Spec-Driven Development, Prompt Engineering, Requirements Engineering mit KI",
    ogTitle: "Intent-Driven Development: Bedeutung vor Implementierung",
    ogDescription: "IDD operationalisiert Kontext, bevor Code entsteht: Intent, Decisions und Scenarios werden zum Projektgedächtnis für agentische Softwareentwicklung.",
    twitterTitle: "Intent-Driven Development (IDD) für Agentic Coding",
    twitterDescription: "Steuerbarer Code statt Prompt-Chaos: IDD macht Bedeutung, Entscheidungen und Szenarien zu operativen Artefakten.",
    url: "https://paioneer-labs.eu/agenticdev",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Intent-Driven Development (IDD) für Agentic Coding",
      serviceType: "Agentische Softwareentwicklung und KI-gestützte Entwicklungsprozesse",
      description: "Beratung und Umsetzung für Intent-Driven Development, IDD Vaults, agentische Coding-Workflows und prüfbare KI-gestützte Softwareentwicklung.",
      provider: {
        "@type": "Organization",
        name: "pAIoneer Labs GmbH",
        url: "https://paioneer-labs.eu"
      },
      areaServed: "DE, NL, EU",
      url: "https://paioneer-labs.eu/agenticdev"
    }
  },
  hero: {
    eyebrow: "Agentic Coding braucht expliziten Kontext",
    title: "Intent-Driven Development",
    claim: "Bedeutung vor Implementierung.",
    lead: "KI schreibt Code. IDD sorgt dafür, dass sie am richtigen Problem arbeitet. Intent-Driven Development macht Ziel, Grenzen, Entscheidungen und Szenarien zu operativen Artefakten für agentische Softwareentwicklung."
  },
  implicitContextProblem: {
    title: "Das Risiko ist impliziter Kontext",
    intro: "Das eigentliche Risiko bei KI-gestützter Softwareentwicklung ist nicht fehlender Code. Das Risiko ist Kontext, der im Kopf, im Chatverlauf oder in verstreuten Tickets verborgen bleibt.",
    items: [
      {
        title: "Unklarer Auftrag",
        description: "Prompts beschreiben oft eine Lösung, aber nicht Ziel, Nicht-Ziele, Erfolgskriterien und Annahmen. Die KI arbeitet dann schnell, aber nicht zwingend in die richtige Richtung."
      },
      {
        title: "Falsche Architektur",
        description: "Technologieentscheidungen entstehen nebenbei: Datenhaltung, Modellwahl, Fehlerfälle und Betrieb werden implizit entschieden, statt bewusst geführt zu werden."
      },
      {
        title: "Nicht prüfbares Ergebnis",
        description: "Ohne Szenarien und Akzeptanzkriterien bleibt offen, ob der Code wirklich richtig reagiert oder nur plausibel aussieht."
      }
    ],
    resolution: "IDD löst genau dieses Problem: Bedeutung wird zuerst operationalisiert, danach wird Code daraus abgeleitet."
  },
  antiVibeCoding: {
    title: "Vibe Coding ist kein Entwicklungsprozess",
    description: "Für Demos kann Bauchgefühl reichen. Für Software mit Betrieb, Wartung, Security, Kosten und Verantwortung reicht es nicht. Agenten brauchen nicht mehr Stimmung, sondern expliziten Kontext.",
    contrasts: [
      "Vibe Coding erzeugt Bewegung. IDD erzeugt Richtung.",
      "Prompting erzeugt Antworten. IDD erzeugt Projektgedächtnis.",
      "Agentic Coding ohne IDD bleibt Aktivität. Agentic Coding mit IDD wird steuerbare Entwicklung."
    ]
  },
  principle: {
    title: "Dokumentation ist kein Nebenprodukt",
    thesis: "In IDD ist Dokumentation ein operatives Artefakt.",
    description: "Dokumentation dient nicht dem Berichtswesen. Sie ist der Arbeitsraum, in dem Ziel, Grenzen, Entscheidungen und erwartetes Verhalten explizit werden, bevor Agenten implementieren.",
    pillarsTitle: "Intent. Decisions. Scenarios. Code.",
    pillarsIntro: "IDD reduziert Softwareentwicklung nicht auf Prompts. Es macht die Bedeutung eines Vorhabens explizit und führt daraus prüfbare Implementierung ab.",
    pillars: [
      {
        title: "Intent",
        description: "Was soll erreicht werden, was ausdrücklich nicht, woran erkennen wir Erfolg?"
      },
      {
        title: "Decisions",
        description: "Welche wichtigen Entscheidungen wurden getroffen, in welchem Kontext und mit welchen Konsequenzen?"
      },
      {
        title: "Scenarios",
        description: "Welches Verhalten wird erwartet, auch in Randfällen, Fehlerfällen und Akzeptanzsituationen?"
      },
      {
        title: "Code",
        description: "Die Implementierung entsteht aus dokumentierter Bedeutung und wird gegen Szenarien und Checks geprüft."
      }
    ]
  },
  vault: {
    title: "Der IDD Vault: Projektgedächtnis statt Prompt-Verlauf",
    description: "Der IDD Vault hält den relevanten Kontext dort, wo Agenten und Menschen ihn wiederverwenden können. Nicht mehr Dateien als nötig. Jede Datei hat eine klare Funktion.",
    rule: "Wenig Artefakte. Klare Funktion. Dauerhafter Kontext.",
    effect: "Der Vault verhindert, dass entscheidender Kontext im Chat, im Kopf oder in verstreuten Tickets verschwindet.",
    treeLines: [
      "/project",
      "  /idd_vault",
      "    intent.md       # Ziel, Nicht-Ziele, Erfolgskriterien, Annahmen",
      "    decisions.md    # Entscheidungen mit Kontext, Begründung, Konsequenzen",
      "    scenarios.md    # Verhalten, Edge Cases, Akzeptanzkriterien",
      "  /code",
      "  AGENTS.md         # Regeln und Erwartungen für Code-Agenten",
      "  README.md         # Einstieg, Setup, Betriebsmodus, Grenzen"
    ]
  },
  outputs: {
    title: "Nicht Text. Steuerbarer Code.",
    description: "Die IDD-Dateien sind kein Bericht. Sie sind Input für konkrete Implementierungsarbeit und Rückführung von Erkenntnissen.",
    items: [
      {
        file: "intent.md",
        result: "Scope",
        description: "Endpoints, Datenmodell, Erfolgskriterien, Nicht-Ziele und Annahmen werden als Entwicklungsrahmen sichtbar."
      },
      {
        file: "decisions.md",
        result: "Architektur",
        description: "Technologie, Betrieb, Persistenz, Modellwahl und Trade-offs werden begründet statt nebenbei entschieden."
      },
      {
        file: "scenarios.md",
        result: "Tests",
        description: "Akzeptanztests, Failure Modes, Statuswechsel und Randfälle entstehen vor oder während der Implementierung."
      },
      {
        file: "AGENTS.md",
        result: "Codequalität",
        description: "Regeln, Patterns, Constraints, Migrations, OpenAPI und erwartete Outputs werden für Agenten explizit."
      },
      {
        file: "README.md",
        result: "Einstieg",
        description: "Setup, Betriebsmodus, Grenzen und Beispielabläufe bleiben verständlich für Menschen und Agenten."
      },
      {
        file: "Code",
        result: "Rückführung",
        description: "Erkenntnisse aus Umsetzung und Prüfung fließen zurück in Vault, Tests und nächste Iterationen."
      }
    ]
  },
  comparison: {
    title: "IDD beginnt vor der Spezifikation",
    intro: "Spec-Driven Development ist stark, wenn das Ziel technisch präzise und stabil ist. IDD setzt davor an: Dort, wo Domäne, Annahmen, Trade-offs und Failure Modes erst explizit gemacht werden müssen.",
    columns: ["Ansatz", "Zentrum", "Stärke", "Grenze"],
    rows: [
      {
        approach: "Prompting",
        center: "Eingabe an ein Modell",
        strength: "Schnell und direkt",
        limit: "Flüchtig, unvollständig und schwer prüfbar"
      },
      {
        approach: "Vibe Coding",
        center: "Gefühl und Iteration",
        strength: "Nützlich für Spielwiesen und Demos",
        limit: "Keine belastbare Steuerung für verantwortbare Software"
      },
      {
        approach: "Spec-Driven Development",
        center: "Technischer Vertrag",
        strength: "Konsistenz gegen eine bereits definierte Spezifikation",
        limit: "Das falsche Problem kann formal korrekt umgesetzt werden"
      },
      {
        approach: "Intent-Driven Development",
        center: "Bedeutung, Ziel und Entscheidungen",
        strength: "Klärt Kontext, Trade-offs und Grenzen vor der Implementierung",
        limit: "Braucht konsequente Pflege von Kontext und Validierung"
      }
    ],
    summary: "Specs sagen, was technisch gelten muss. IDD begründet, warum genau diese Richtung gebaut werden soll."
  },
  workflow: {
    title: "Klären. Entscheiden. Formulieren. Bauen. Zurückführen.",
    description: "IDD ist kein Wasserfall. IDD ist kleine, kontrollierte Iteration mit dauerhaftem Kontext.",
    steps: [
      {
        title: "Intent klären",
        description: "Ziel, Nicht-Ziele, Erfolgskriterien und Annahmen werden sichtbar, bevor Code entsteht."
      },
      {
        title: "Entscheidungen dokumentieren",
        description: "Architektur- und Produktentscheidungen werden mit Kontext, Begründung und Konsequenz festgehalten."
      },
      {
        title: "Szenarien formulieren",
        description: "Normales Verhalten, Edge Cases, Failure Modes und Akzeptanzkriterien werden prüfbar beschrieben."
      },
      {
        title: "Agenten implementieren lassen",
        description: "KI-Agenten arbeiten aus explizitem Kontext statt aus isolierten Prompts."
      },
      {
        title: "Ergebnis prüfen und zurückführen",
        description: "Erkenntnisse aus Code, Tests und Betrieb aktualisieren Vault und nächste Iteration."
      }
    ]
  },
  impact: {
    title: "Warum das für echte Projekte zählt",
    description: "IDD macht KI-gestützte Entwicklung anschlussfähig für Teams, Betrieb und Qualitätssicherung.",
    items: [
      "Weniger Rework, weil Ziele und Nicht-Ziele sichtbar sind.",
      "Bessere Architektur, weil Entscheidungen begründet werden.",
      "Bessere Tests, weil Szenarien vor dem Code entstehen.",
      "Bessere Zusammenarbeit, weil Kontext nicht im Chat verschwindet.",
      "Bessere Agenten-Ergebnisse, weil jeder Schritt auf expliziten Artefakten basiert."
    ]
  },
  contact: {
    title: "IDD in agentische Entwicklungsprozesse bringen",
    description: "Wenn KI-gestützte Entwicklung mehr sein soll als Experimente, braucht sie ein operatives Kontextmodell.",
    details: "Wir unterstützen beim Aufbau von IDD Vaults, Agenten-Regeln, Szenarien und prüfbaren Entwicklungsabläufen.",
    button: "IDD besprechen",
    subject: "Intent-Driven Development besprechen",
    email: "info@paioneer.eu"
  }
};
